import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { solvePendulum } from './pendulumMath';

interface PendulumCanvasProps {
  lengthM: number;
  angleDeg: number;
  dampingCoefficient?: number;
  mass?: number;
  paused?: boolean;
  showGuide?: boolean;
  onTime?: (t: number) => void;
}

const PendulumCanvas: React.FC<PendulumCanvasProps> = ({
  lengthM,
  angleDeg,
  dampingCoefficient = 0.02,
  mass = 1,
  paused = false,
  showGuide = true,
  onTime,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [phase, setPhase] = useState(0);

  const solution = useMemo(() => solvePendulum({
    L: lengthM,
    theta0: (angleDeg * Math.PI) / 180,
    omega0: 0,
    dampingCoefficient,
    dt: 0.001,
    duration: 8,
    sampleRate: 60,
    mass,
  }), [lengthM, angleDeg, dampingCoefficient, mass]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const renderFrame = () => {
      const width = canvas.width;
      const height = canvas.height;
      const pivotX = width / 2;
      const pivotY = 58;
      const bobRadius = 18;
      const stringLength = Math.min(width * 0.35, height * 0.5);
      const normalized = solution.states[Math.min(Math.floor((phase / 100) * (solution.states.length - 1)), solution.states.length - 1)] ?? solution.states[0];
      const angle = normalized.theta;
      const x = pivotX + Math.sin(angle) * stringLength;
      const y = pivotY + Math.cos(angle) * stringLength;

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#f4f7fb';
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = '#2c3e50';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(pivotX, pivotY);
      ctx.lineTo(x, y);
      ctx.stroke();

      ctx.fillStyle = '#8e44ad';
      ctx.beginPath();
      ctx.arc(x, y, bobRadius, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#2c3e50';
      ctx.fillRect(pivotX - 6, pivotY - 2, 12, 18);

      if (showGuide) {
        ctx.strokeStyle = '#9aa9b7';
        ctx.setLineDash([4, 6]);
        ctx.beginPath();
        ctx.moveTo(pivotX, pivotY);
        ctx.lineTo(pivotX, y + 120);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#3d4d5c';
        ctx.font = '12px sans-serif';
        ctx.fillText(`θ = ${(angle * 180 / Math.PI).toFixed(1)}°`, 24, 30);
      }
    };

    renderFrame();

    if (paused) return;
    let raf = 0;
    const tick = () => {
      setPhase((previous) => {
        const next = previous + 1;
        const safe = next % 100;
        onTime?.(safe / 100);
        return safe;
      });
      renderFrame();
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(raf);
  }, [angleDeg, dampingCoefficient, lengthM, mass, onTime, paused, phase, showGuide, solution]);

  return (
    <Box sx={{ border: '1px solid #dfe7f2', borderRadius: 2, overflow: 'hidden', background: '#f5f7fb' }}>
      <canvas ref={canvasRef} width={720} height={360} style={{ width: '100%', height: 'auto', display: 'block' }} aria-label="Live pendulum simulation" />
      <Typography variant="caption" sx={{ display: 'block', px: 2, py: 1, color: '#4a5f76' }}>
        Natural pendulum motion with damping and nonlinear swing dynamics.
      </Typography>
    </Box>
  );
};

export default PendulumCanvas;
